import type {
  KnowledgeBase,
  KnowledgeBaseAttachment,
  KnowledgeBaseAttachmentWithContent,
  KnowledgeBaseCreatePayload,
  KnowledgeBaseUpdatePayload,
} from "@repo/app-types/entities/knowledgeBase";
import { getAxiosInstanceWithAuth } from "../axiosConfig.js";
import {
  buildEntityTag,
  buildListTag,
  getTagsInvalidatedByCreate,
  getTagsInvalidatedByDelete,
  getTagsInvalidatedByUpdate,
  getTagsProvidedByEntity,
  getTagsProvidedByList,
  TagType,
} from "../helpers/tags.js";
import { api } from "../redux.js";

const knowledgeBaseApi = api.injectEndpoints({
  endpoints: (builder) => ({
    allKnowledgeBases: builder.query<
      KnowledgeBase[],
      void,
      { data: { knowledge_bases: KnowledgeBase[] } }
    >({
      query: () => "/knowledge-bases",
      transformResponse: (response) => response.data.knowledge_bases,
      providesTags: (result) =>
        getTagsProvidedByList(TagType.KnowledgeBase, result),
    }),

    getKnowledgeBase: builder.query<
      KnowledgeBase,
      string,
      {
        data: {
          knowledge_base: KnowledgeBase;
        };
      }
    >({
      query: (id) => `/knowledge-bases/${encodeURIComponent(id)}`,
      transformResponse: (response) => response.data.knowledge_base,
      providesTags: (_result, _error, id) =>
        getTagsProvidedByEntity(TagType.KnowledgeBase, id),
    }),

    createKnowledgeBase: builder.mutation<
      KnowledgeBase,
      KnowledgeBaseCreatePayload,
      { data: { knowledge_base: KnowledgeBase } }
    >({
      query: (body) => ({
        method: "POST",
        url: "/knowledge-bases",
        body,
      }),
      transformResponse: (response) => response.data.knowledge_base,
      invalidatesTags: () => getTagsInvalidatedByCreate(TagType.KnowledgeBase),
    }),

    updateKnowledgeBase: builder.mutation<
      KnowledgeBase,
      { id: string; data: KnowledgeBaseUpdatePayload },
      { data: { knowledge_base: KnowledgeBase } }
    >({
      query: ({ id, data }) => ({
        method: "PATCH",
        url: `/knowledge-bases/${encodeURIComponent(id)}`,
        body: data,
      }),
      transformResponse: (response) => response.data.knowledge_base,
      invalidatesTags: (_result, _error, { id }) =>
        getTagsInvalidatedByUpdate(TagType.KnowledgeBase, id),
    }),

    deleteKnowledgeBase: builder.mutation<void, string>({
      query: (id) => ({
        method: "DELETE",
        url: `/knowledge-bases/${encodeURIComponent(id)}`,
      }),
      invalidatesTags: (_result, _error, id) =>
        getTagsInvalidatedByDelete(TagType.KnowledgeBase, id),
    }),

    getKnowledgeBaseAttachments: builder.query<
      KnowledgeBaseAttachment[],
      string,
      { data: { attachments: KnowledgeBaseAttachment[] } }
    >({
      query: (knowledgeBaseId) =>
        `/knowledge-bases/${encodeURIComponent(knowledgeBaseId)}/attachments`,
      transformResponse: (response) => response.data.attachments,
      providesTags: (result, _error, knowledgeBaseId) => {
        const entityTags = (result || [])?.map((attachment) =>
          buildEntityTag(
            TagType.KnowledgeBaseAttachment,
            `${knowledgeBaseId}_${attachment.id}`,
          ),
        );

        return [
          ...entityTags,
          buildListTag(TagType.KnowledgeBaseAttachment, [knowledgeBaseId]),
        ];
      },
    }),

    uploadKnowledgeBaseAttachment: builder.mutation<
      KnowledgeBaseAttachment[],
      { knowledgeBaseId: string; file: File }
    >({
      queryFn: async ({ knowledgeBaseId, file }, _queryApi, _extraOptions) => {
        const formData = new FormData();
        formData.append("file", file);

        try {
          const axios = await getAxiosInstanceWithAuth();
          const response = await axios.post<{
            data: { attachments: KnowledgeBaseAttachment[] };
          }>(
            `/knowledge-bases/${encodeURIComponent(knowledgeBaseId)}/attachments`,
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            },
          );
          return { data: response.data.data.attachments };
        } catch (error: any) {
          return {
            error: {
              status: error.response?.status,
              data: error.response?.data || error.message,
            },
          };
        }
      },
      invalidatesTags: (_result, _error, { knowledgeBaseId }) => [
        ...getTagsInvalidatedByUpdate(TagType.KnowledgeBase, knowledgeBaseId),
        buildListTag(TagType.KnowledgeBaseAttachment, [knowledgeBaseId]),
      ],
    }),

    deleteKnowledgeBaseAttachment: builder.mutation<
      KnowledgeBaseAttachment[],
      { knowledgeBaseId: string; attachmentId: string },
      { data: { attachments: KnowledgeBaseAttachment[] } }
    >({
      query: ({ knowledgeBaseId, attachmentId }) => ({
        method: "DELETE",
        url: `/knowledge-bases/${encodeURIComponent(knowledgeBaseId)}/attachments/${encodeURIComponent(attachmentId)}`,
      }),
      transformResponse: (response) => response.data.attachments,
      invalidatesTags: (_result, _error, { knowledgeBaseId, attachmentId }) => [
        ...getTagsInvalidatedByDelete(
          TagType.KnowledgeBaseAttachment,
          attachmentId,
        ),
        buildListTag(TagType.KnowledgeBaseAttachment, [knowledgeBaseId]),
      ],
    }),

    knowledgeBaseAttachmentById: builder.query<
      KnowledgeBaseAttachmentWithContent,
      { attachmentId: string },
      {
        data: {
          knowledge_base_attachment_with_content: KnowledgeBaseAttachmentWithContent;
        };
      }
    >({
      query: ({ attachmentId }) =>
        `/knowledge-bases/attachments/${encodeURIComponent(attachmentId)}`,
      transformResponse: (response) =>
        response.data.knowledge_base_attachment_with_content,
      providesTags: (_result, _error, { attachmentId }) =>
        getTagsProvidedByEntity(TagType.KnowledgeBaseAttachment, attachmentId),
    }),

    getKnowledgeBaseAttachmentWithContent: builder.query<
      KnowledgeBaseAttachmentWithContent,
      { knowledgeBaseId: string; attachmentId: string },
      {
        data: {
          knowledge_base_attachment_with_content: KnowledgeBaseAttachmentWithContent;
        };
      }
    >({
      query: ({ knowledgeBaseId, attachmentId }) =>
        `/knowledge-bases/${encodeURIComponent(knowledgeBaseId)}/attachments/${encodeURIComponent(attachmentId)}`,
      transformResponse: (response) =>
        response.data.knowledge_base_attachment_with_content,
      providesTags: (_result, _error, { knowledgeBaseId, attachmentId }) =>
        getTagsProvidedByEntity(TagType.KnowledgeBaseAttachment, attachmentId),
    }),

    updateKnowledgeBaseAttachmentContent: builder.mutation<
      KnowledgeBaseAttachmentWithContent,
      { knowledgeBaseId: string; attachmentId: string; body: string },
      {
        data: {
          knowledge_base_attachment_with_content: KnowledgeBaseAttachmentWithContent;
        };
      }
    >({
      query: ({ knowledgeBaseId, attachmentId, body }) => ({
        method: "PATCH",
        url: `/knowledge-bases/${encodeURIComponent(knowledgeBaseId)}/attachments/${encodeURIComponent(attachmentId)}`,
        body: { body },
      }),
      transformResponse: (response) =>
        response.data.knowledge_base_attachment_with_content,
      invalidatesTags: (_result, _error, { knowledgeBaseId, attachmentId }) =>
        getTagsInvalidatedByUpdate(
          TagType.KnowledgeBaseAttachment,
          attachmentId,
          {
            inLists: [{ filteredBy: [knowledgeBaseId] }],
          },
        ),
    }),
  }),
});

export const {
  useAllKnowledgeBasesQuery,
  useGetKnowledgeBaseQuery,
  useGetKnowledgeBaseAttachmentsQuery,
  useGetKnowledgeBaseAttachmentWithContentQuery,
  useLazyKnowledgeBaseAttachmentByIdQuery,
  useCreateKnowledgeBaseMutation,
  useUpdateKnowledgeBaseMutation,
  useDeleteKnowledgeBaseMutation,
  useUploadKnowledgeBaseAttachmentMutation,
  useDeleteKnowledgeBaseAttachmentMutation,
  useUpdateKnowledgeBaseAttachmentContentMutation,
} = knowledgeBaseApi;
